/**
 * Browser-Capture einer Quelle (Doc 04 §1 + Doc 13 §3): Mikrofon oder
 * fremder MediaStream (Tab-Audio, Doc 15 §1.2) → AudioWorklet (Kanalwahl,
 * Resampling 16 kHz, Int16, 1280er-Frames, VAD, Gate) → Capture-Socket.
 * Eine Instanz = eine Quelle = ein Socket.
 *
 * Events (CustomEvent.detail):
 *   statechange → { state, error? }
 *   level       → { rms, vad, dropped }       (~10/s)
 *   speech      → { state: 'start'|'end' }
 *   started     → CaptureStarted
 *   error       → { code, message, fatal }
 *   ended       → { reason: 'track-ended' }   (nur `stream`: Spur beendet, z. B.
 *                 Teilen in der Browser-Leiste gestoppt; danach folgt `stopped`)
 */
import type { CaptureMode, CaptureStart, CaptureStarted, RegieAuth, SourceConfig } from '@voice/protocol';
import { DEFAULT_AUDIO } from '@voice/protocol/runtime';
import { CaptureSocket } from './CaptureSocket.js';
import { DEFAULT_VAD_CONFIG, type VadConfig } from './dsp.js';
import { CaptureInputError, selectCaptureInput } from './input.js';
import { createWorkletUrl, WORKLET_PROCESSOR_NAME, type WorkletOptions } from './worklet.js';

export type VoiceCaptureState = 'idle' | 'requesting-mic' | 'connecting' | 'streaming' | 'reconnecting' | 'stopped' | 'error';

export interface VoiceCaptureConfig {
  /** ws(s)://host/capture */
  gatewayUrl: string;
  joinToken?: string;
  auth?: RegieAuth;
  /** Pflicht bei `auth` */
  sessionId?: string;
  source: SourceConfig;
  /** konkretes Eingabegerät (enumerateDevices().deviceId) */
  deviceId?: string;
  /**
   * Alternative zu `deviceId`: fertiger Stream des Aufrufers (z. B. Tab-Audio
   * aus `getDisplayMedia`). Dann kein eigenes `getUserMedia`; benutzt wird die
   * erste Audio-Spur, im Downmix (keine Kanalwahl, kein EC/NS/AGC). Die Spur
   * gehört weiter dem Aufrufer und wird beim Stop nicht beendet. Endet sie,
   * stoppt die Capture mit `ended`.
   */
  stream?: MediaStream;
  frameSamples?: number;
  preRollMs?: number;
  /** VAD-Feineinstellung (z. B. `minSpeechMs`, Default 250 ms vor `speech start`), Rest aus DEFAULT_VAD_CONFIG */
  vad?: Partial<VadConfig>;
  WebSocketImpl?: typeof WebSocket;
}

export class VoiceCapture extends EventTarget {
  private _state: VoiceCaptureState = 'idle';
  private _level = 0;
  private _speaking = false;
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  /** true = Stream per getUserMedia selbst geholt → Spuren beim Teardown beenden */
  private ownsStream = false;
  private externalTrack: MediaStreamTrack | null = null;
  private stopping: Promise<void> | null = null;
  private readonly onTrackEnded = (): void => {
    this.dispatchEvent(new CustomEvent('ended', { detail: { reason: 'track-ended' } }));
    void this.stop();
  };
  private node: AudioWorkletNode | null = null;
  private sink: GainNode | null = null;
  private socket: CaptureSocket | null = null;
  private lastSample = 0;
  private baseSample = 0;
  private workletUrl: string | null = null;
  private mode: CaptureMode;

  constructor(private readonly cfg: VoiceCaptureConfig) {
    super();
    this.mode = cfg.source.mode;
  }

  get state(): VoiceCaptureState {
    return this._state;
  }
  get level(): number {
    return this._level;
  }
  get speaking(): boolean {
    return this._speaking;
  }
  get dropped(): number {
    return this.socket?.dropped ?? 0;
  }

  static async listMicrophones(): Promise<MediaDeviceInfo[]> {
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.filter((d) => d.kind === 'audioinput');
  }

  async start(): Promise<void> {
    if (this._state !== 'idle' && this._state !== 'stopped' && this._state !== 'error') return;
    let channel = this.cfg.source.device?.channel ?? null;
    try {
      const choice = selectCaptureInput(this.cfg);
      if (choice.type === 'stream') {
        // Nur die gewählte Spur in den Graphen (createMediaStreamSource nähme sonst irgendeine Audio-Spur).
        this.stream = typeof MediaStream === 'function' ? new MediaStream([choice.track]) : (this.cfg.stream as MediaStream);
        this.ownsStream = false;
        this.externalTrack = choice.track;
        choice.track.addEventListener('ended', this.onTrackEnded);
        channel = null; // Tab-/Fenster-Audio: immer Downmix
      } else {
        this.setState('requesting-mic');
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: choice.constraints });
        this.ownsStream = true;
      }
      this.ctx = new AudioContext();
      this.workletUrl = createWorkletUrl();
      await this.ctx.audioWorklet.addModule(this.workletUrl);
      const input = this.ctx.createMediaStreamSource(this.stream);
      const trackChannels = this.stream.getAudioTracks()[0]?.getSettings().channelCount ?? 1;
      const options: WorkletOptions = {
        frameSamples: this.cfg.frameSamples ?? DEFAULT_AUDIO.frameSamples,
        targetRate: DEFAULT_AUDIO.sampleRate,
        channel: channel !== null && channel < trackChannels ? channel : null,
        mode: this.mode,
        preRollMs: this.cfg.preRollMs ?? 300,
        vad: { ...DEFAULT_VAD_CONFIG, ...this.cfg.vad, sampleRate: DEFAULT_AUDIO.sampleRate },
      };
      this.node = new AudioWorkletNode(this.ctx, WORKLET_PROCESSOR_NAME, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: Math.max(1, trackChannels),
        channelCountMode: 'explicit',
        channelInterpretation: 'discrete',
        processorOptions: options,
      });
      this.node.port.onmessage = (e: MessageEvent) => this.onWorklet(e.data);
      // Stummer Ausgang hält den Graphen in allen Browsern aktiv.
      this.sink = this.ctx.createGain();
      this.sink.gain.value = 0;
      input.connect(this.node);
      this.node.connect(this.sink);
      this.sink.connect(this.ctx.destination);
    } catch (err) {
      this.teardownAudio();
      if (err instanceof CaptureInputError) this.dispatchEvent(new CustomEvent('error', { detail: { code: err.code, message: err.message, fatal: true } }));
      this.setState('error', err instanceof Error ? err.message : String(err));
      throw err;
    }

    this.setState('connecting');
    this.socket = new CaptureSocket({
      url: this.cfg.gatewayUrl,
      startMessage: () => this.startMessage(),
      ...(this.cfg.WebSocketImpl ? { WebSocketImpl: this.cfg.WebSocketImpl } : {}),
      onState: (s, detail) => {
        if (s === 'streaming') this.setState('streaming');
        else if (s === 'reconnecting') this.setState('reconnecting');
        else if (s === 'error') this.setState('error', detail?.message ?? detail?.code);
      },
      onStarted: (msg, reconnect) => this.onStarted(msg, reconnect),
      onError: (code, message, fatal) => this.dispatchEvent(new CustomEvent('error', { detail: { code, message, fatal } })),
    });
    try {
      await this.socket.connect();
    } catch (err) {
      this.teardownAudio();
      throw err;
    }
  }

  /** Idempotent: ein zweiter Aufruf (Nutzer-Stopp + Spurende) wartet auf den ersten. */
  stop(): Promise<void> {
    if (this._state === 'stopped') return Promise.resolve();
    this.stopping ??= this.doStop().finally(() => (this.stopping = null));
    return this.stopping;
  }

  private async doStop(): Promise<void> {
    if (this._speaking) this.socket?.sendSpeech('end', this.tOf(this.lastSample));
    this._speaking = false;
    await this.socket?.stop();
    this.socket = null;
    this.teardownAudio();
    this.setState('stopped');
  }

  /** Push-to-Talk gedrückt (nur im Modus ptt wirksam). */
  pttDown(): void {
    this.node?.port.postMessage({ type: 'ptt', down: true });
  }

  pttUp(): void {
    this.node?.port.postMessage({ type: 'ptt', down: false });
  }

  setMode(mode: CaptureMode): void {
    this.mode = mode;
    this.node?.port.postMessage({ type: 'mode', mode });
  }

  private startMessage(): CaptureStart {
    const msg: CaptureStart = {
      v: 2,
      type: 'start',
      source: { ...this.cfg.source, mode: this.mode },
      audio: { ...DEFAULT_AUDIO, frameSamples: this.cfg.frameSamples ?? DEFAULT_AUDIO.frameSamples },
    };
    if (this.cfg.joinToken) msg.joinToken = this.cfg.joinToken;
    if (this.cfg.auth) msg.auth = this.cfg.auth;
    if (this.cfg.sessionId) msg.sessionId = this.cfg.sessionId;
    return msg;
  }

  private onStarted(msg: CaptureStarted, reconnect: boolean): void {
    this.baseSample = this.lastSample;
    // Spricht gerade jemand (Gate offen), braucht das Gateway nach dem Reconnect ein neues speech start.
    if (reconnect && this._speaking) this.socket?.sendSpeech('start', 0);
    this.dispatchEvent(new CustomEvent('started', { detail: msg }));
  }

  private tOf(sample: number): number {
    return Math.max(0, ((sample - this.baseSample) * 1000) / DEFAULT_AUDIO.sampleRate);
  }

  private onWorklet(data: { type: string; buf?: ArrayBuffer; start?: number; state?: 'start' | 'end'; sample?: number; rms?: number; vad?: boolean }): void {
    switch (data.type) {
      case 'frame':
        if (data.buf) {
          this.socket?.sendFrame(new Int16Array(data.buf));
          this.lastSample = Math.max(this.lastSample, (data.start ?? 0) + data.buf.byteLength / 2);
        }
        break;
      case 'speech':
        this._speaking = data.state === 'start';
        this.socket?.sendSpeech(data.state ?? 'end', this.tOf(data.sample ?? this.lastSample));
        this.dispatchEvent(new CustomEvent('speech', { detail: { state: data.state } }));
        break;
      case 'level':
        this._level = data.rms ?? 0;
        this.lastSample = Math.max(this.lastSample, data.sample ?? 0);
        this.socket?.sendLevel(this._level);
        this.dispatchEvent(new CustomEvent('level', { detail: { rms: this._level, vad: Boolean(data.vad), dropped: this.dropped } }));
        break;
    }
  }

  private teardownAudio(): void {
    try {
      this.node?.disconnect();
      this.sink?.disconnect();
    } catch {
      /* egal */
    }
    this.externalTrack?.removeEventListener('ended', this.onTrackEnded);
    // Fremde Spuren (stream aus der Config) gehören dem Aufrufer: nicht beenden.
    if (this.ownsStream) this.stream?.getTracks().forEach((t) => t.stop());
    void this.ctx?.close().catch(() => undefined);
    if (this.workletUrl) URL.revokeObjectURL(this.workletUrl);
    this.node = null;
    this.sink = null;
    this.stream = null;
    this.ownsStream = false;
    this.externalTrack = null;
    this.ctx = null;
    this.workletUrl = null;
  }

  private setState(s: VoiceCaptureState, error?: string): void {
    if (this._state === s && !error) return;
    this._state = s;
    this.dispatchEvent(new CustomEvent('statechange', { detail: { state: s, error } }));
  }
}
